import { DAY_MAPPING, DAY_MAPPING_REVERSE } from '../constants/tabConfig';
import { TransformedPercentageRow, PercentageData } from '../types';

export const transformDailyPercentageData = (apiData: any[]): TransformedPercentageRow[] => {
  if (!apiData || !Array.isArray(apiData)) return [];

  console.log('Transforming daily percentage data:', apiData);

  const percentages: PercentageData = {
    sun: { default: '0.00%', override: '0.00%' },
    mon: { default: '0.00%', override: '0.00%' },
    tue: { default: '0.00%', override: '0.00%' },
    wed: { default: '0.00%', override: '0.00%' },
    thu: { default: '0.00%', override: '0.00%' },
    fri: { default: '0.00%', override: '0.00%' },
    sat: { default: '0.00%', override: '0.00%' },
  };

  // Track which days have actual backend overrides (not null)
  const actualOverrides: { [key: string]: boolean } = {};

  // Map API data to percentages
  apiData.forEach(item => {
    const dayKey = DAY_MAPPING[item.day_num as keyof typeof DAY_MAPPING];
    if (dayKey) {
      const defaultValue = item.daily_pct !== null && item.daily_pct !== undefined 
        ? (item.daily_pct <= 1 ? item.daily_pct * 100 : item.daily_pct).toFixed(2)
        : '0.00';
      
      // Check if override_daily_pct is not null (has actual backend override)
      const hasOverride = item.override_daily_pct !== null && item.override_daily_pct !== undefined;
      actualOverrides[dayKey] = hasOverride;
      
      const overrideValue = hasOverride
        ? (item.override_daily_pct <= 1 ? item.override_daily_pct * 100 : item.override_daily_pct).toFixed(2)
        : defaultValue; // Copy default to override when no backend override exists

      percentages[dayKey as keyof PercentageData] = {
        default: `${defaultValue}%`,
        override: `${overrideValue}%`
      };
    }
  });

  // Calculate total
  let total = 0;
  Object.values(percentages).forEach(day => {
    const overrideValue = parseFloat(day.override.replace('%', ''));
    const defaultValue = parseFloat(day.default.replace('%', ''));
    total += overrideValue > 0 ? overrideValue : defaultValue;
  });

  const result = [{
    id: '1',
    percentages,
    total: `${total.toFixed(2)}%`,
    __actualOverrides: actualOverrides // Add metadata about which days have backend overrides
  }];

  console.log('Transformed percentage data result with actualOverrides:', result);
  return result;
};

// New transformer function for actual daily cases data
export const transformDailyCasesData = (apiData: any[]): TransformedPercentageRow[] => {
  if (!apiData || !Array.isArray(apiData)) return [];

  console.log('Transforming daily cases data:', apiData);

  const percentages: PercentageData = {
    sun: { default: '0.00', override: '0.00' },
    mon: { default: '0.00', override: '0.00' },
    tue: { default: '0.00', override: '0.00' },
    wed: { default: '0.00', override: '0.00' },
    thu: { default: '0.00', override: '0.00' },
    fri: { default: '0.00', override: '0.00' },
    sat: { default: '0.00', override: '0.00' },
  };

  // Track which days have actual backend overrides (not null)
  const actualOverrides: { [key: string]: boolean } = {};

  // Map API data to daily cases
  apiData.forEach(item => {
    const dayKey = DAY_MAPPING[item.day_num as keyof typeof DAY_MAPPING];
    if (dayKey) {
      const defaultValue = item.default_daily_cases !== null && item.default_daily_cases !== undefined 
        ? Number(item.default_daily_cases).toFixed(2)
        : '0.00';
      
      // Check if override_daily_cases is not null (has actual backend override)
      const hasOverride = item.override_daily_cases !== null && item.override_daily_cases !== undefined;
      actualOverrides[dayKey] = hasOverride;
      
      const overrideValue = hasOverride
        ? Number(item.override_daily_cases).toFixed(2)
        : defaultValue; // Copy default to override when no backend override exists

      percentages[dayKey as keyof PercentageData] = {
        default: defaultValue,
        override: overrideValue
      };
    }
  });

  // Calculate total from override values only
  let total = 0;
  Object.values(percentages).forEach(day => {
    const overrideValue = parseFloat(day.override) || 0;
    total += overrideValue;
  });

  const result = [{
    id: '1',
    percentages,
    total: total.toFixed(2),
    __actualOverrides: actualOverrides // Add metadata about which days have backend overrides
  }];

  console.log('Transformed daily cases data result with actualOverrides:', result);
  return result;
};

export const transformHistoricalHoursData = (apiData: any[]): any[] => {
  if (!apiData || !Array.isArray(apiData)) return [];

  console.log('Transforming historical hours data:', apiData);

  const zoneData: { [key: string]: any } = {};
  
  apiData.forEach(item => {
    console.log('Processing item:', item);
    
    const zone = item.vp_zone || item.VP_Zone || item.vpZone || item.zone;
    
    if (!zone) {
      console.warn('No zone field found in item:', item);
      return;
    }

    if (!zoneData[zone]) {
      zoneData[zone] = {
        id: zone.toLowerCase(),
        zone: zone,
        historicalHours: {
          sun: '', mon: '', tue: '', wed: '', thu: '', fri: '', sat: ''
        },
        fixedHours: {
          sun: '', mon: '', tue: '', wed: '', thu: '', fri: '', sat: ''
        }
      };
    }

    const dayKey = DAY_MAPPING[item.day_num as keyof typeof DAY_MAPPING];
    if (dayKey) {
      // Set historical hours (show as number if available, otherwise empty)
      const historicalValue = item.historical_hours != null 
        ? item.historical_hours.toString() 
        : '0';
      zoneData[zone].historicalHours[dayKey] = historicalValue;
      
      // Set fixed hours - if null in backend, copy historical_hours value
      zoneData[zone].fixedHours[dayKey] = item.fixed_hours != null 
        ? item.fixed_hours.toString() 
        : historicalValue; // Copy historical_hours when fixed_hours is null
      
      // Track which days have actual fixed_hours from backend (not null)
      if (!zoneData[zone].__actualFixedHours) {
        zoneData[zone].__actualFixedHours = {};
      }
      zoneData[zone].__actualFixedHours[dayKey] = item.fixed_hours !== null;
      
      // Initialize __changed metadata to track user modifications
      if (!zoneData[zone].__changed) {
        zoneData[zone].__changed = {};
      }
      zoneData[zone].__changed[dayKey] = false; // Initially no changes
    }
  });

  const result = Object.values(zoneData);
  console.log('Transformed historical hours data result:', result);
  return result;
};

export const transformZoneDistributionData = (apiData: any[], type: 'DSD' | 'NON-DSD'): any[] => {
  if (!apiData || !Array.isArray(apiData)) return [];

  console.log('Transforming zone distribution data:', apiData, 'for type:', type);

  const zoneGroups = apiData.reduce((acc, item) => {
    // Support both VP_Zone (location settings) and vpZone (schedule settings API)
    const zone = item.VP_Zone || item.vpZone;
    if (!zone) return acc;
    const zoneKey = zone.toLowerCase();
    if (!acc[zoneKey]) {
      acc[zoneKey] = [];
    }
    acc[zoneKey].push(item);
    return acc;
  }, {} as Record<string, any[]>);

  const result = Object.entries(zoneGroups).map(([zoneId, zoneItems]) => {
    const zoneItemsArray = zoneItems as any[];
    const zoneName = zoneItemsArray[0]?.VP_Zone || zoneItemsArray[0]?.vpZone || zoneId.toUpperCase();
    
    const percentages = {
      sun: { default: '0.00%', override: '0.00%' },
      mon: { default: '0.00%', override: '0.00%' },
      tue: { default: '0.00%', override: '0.00%' },
      wed: { default: '0.00%', override: '0.00%' },
      thu: { default: '0.00%', override: '0.00%' },
      fri: { default: '0.00%', override: '0.00%' },
      sat: { default: '0.00%', override: '0.00%' },
    };
    
    let kcph = { default: '0.00', override: '0.00' };
    const actualOverrides: { [key: string]: boolean } = {};

    zoneItemsArray.forEach((item: any) => {
      // Support both snake_case (location settings) and camelCase (schedule settings API)
      const dayNum = item.day_num ?? item.dayNum;
      const dayKey = DAY_MAPPING[dayNum as keyof typeof DAY_MAPPING];
      if (dayKey) {
        // Handle percentage fields - support both snake_case and camelCase
        const defaultPct = item.default_pct ?? item.defaultPct;
        const overridePct = item.override_pct ?? item.overridePct;
        const defaultCases = item.default_cases ?? item.defaultCases;
        const overrideCases = item.override_cases ?? item.overrideCases;
        const dailyPct = item.daily_pct ?? item.dailyPct;
        const overrideDailyPct = item.override_daily_pct ?? item.overrideDailyPct;
        const defaultKcphRaw = item.default_kcph ?? item.defaultKcph ?? item.kcph;
        const overrideKcphRaw = item.override_kcph ?? item.overrideKcph;

        // Handle percentage fields (default_pct and override_pct)
        let defaultValue = '0.00%';
        if (defaultPct !== null && defaultPct !== undefined) {
          const pctValue = (defaultPct <= 1 ? defaultPct * 100 : defaultPct).toFixed(2);
          defaultValue = `${pctValue}%`;
          percentages[dayKey as keyof typeof percentages].default = defaultValue;
        }
        
        if (overridePct !== null && overridePct !== undefined) {
          const overridePctValue = (overridePct <= 1 ? overridePct * 100 : overridePct).toFixed(2);
          percentages[dayKey as keyof typeof percentages].override = `${overridePctValue}%`;
          actualOverrides[dayKey] = true;
        } else {
          percentages[dayKey as keyof typeof percentages].override = defaultValue;
          actualOverrides[dayKey] = false;
        }

        // Handle cases fields (if they exist)
        if (defaultCases !== null && defaultCases !== undefined) {
          percentages[dayKey as keyof typeof percentages].default = `${defaultCases}`;
        }
        
        if (overrideCases !== null && overrideCases !== undefined) {
          percentages[dayKey as keyof typeof percentages].override = `${overrideCases}`;
          actualOverrides[dayKey] = true;
        } else if (defaultCases !== null && defaultCases !== undefined) {
          percentages[dayKey as keyof typeof percentages].override = `${defaultCases}`;
          actualOverrides[dayKey] = false;
        }

        // Handle legacy daily_pct fields for backward compatibility
        if (dailyPct !== null && dailyPct !== undefined) {
          const pctValue = (dailyPct <= 1 ? dailyPct * 100 : dailyPct).toFixed(2);
          percentages[dayKey as keyof typeof percentages].default = `${pctValue}%`;
        }
        
        if (overrideDailyPct !== null && overrideDailyPct !== undefined) {
          const overridePctValue = (overrideDailyPct <= 1 ? overrideDailyPct * 100 : overrideDailyPct).toFixed(2);
          percentages[dayKey as keyof typeof percentages].override = `${overridePctValue}%`;
          actualOverrides[dayKey] = true;
        }

        // Handle KCPH fields
        let defaultKcphValue = '0.00';
        if (defaultKcphRaw !== null && defaultKcphRaw !== undefined) {
          defaultKcphValue = defaultKcphRaw.toString();
          kcph.default = defaultKcphValue;
        }
        
        if (overrideKcphRaw !== null && overrideKcphRaw !== undefined) {
          kcph.override = overrideKcphRaw.toString();
          actualOverrides['kcph'] = true;
        } else {
          kcph.override = defaultKcphValue;
          actualOverrides['kcph'] = false;
        }
      }
    });

    return {
      id: zoneId,
      zoneName: zoneName,
      percentages,
      kcph,
      __actualOverrides: actualOverrides, // Track which overrides are from backend
    };
  });

  console.log('Transformed zone distribution data result:', result);
  return result;
};

export const transformScheduleForecastData = (apiData: any[]): any[] => {
  if (!apiData || !Array.isArray(apiData)) return [];

  console.log('Transforming schedule forecast data:', apiData);

  const workTypes = Array.from(new Set(apiData.map(item => item.workType)));
  
  const workTypesObject: Record<string, { default: string; override: string }> = {};
  workTypes.forEach(workType => {
    const apiItem = apiData.find(item => item.workType === workType);
    workTypesObject[workType] = {
      default: apiItem?.fcastedCases || '0',
      override: apiItem?.overrideCases || ''
    };
  });

  const result = [
    {
      id: 'default',
      label: 'Default Volume Forecast by Week',
      workTypes: workTypesObject,
    },
    {
      id: 'override',
      label: 'Override Volume Forecast by Week',
      workTypes: workTypesObject,
    },
  ];

  console.log('Transformed schedule forecast data result:', result);
  return result;
};
